import { IPublisher } from '@/Interfaces/Publisher.interface';
import SearchIcon from '@/components/Icons/SearchIcon';
import SelectAllOption from '@/components/TabSelect/SelectAllOption';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { GREY } from '@/constants/color';
import useListSearch from '@/hooks/useListSearch';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  publishers: IPublisher[];
  disabled?: boolean;
  className?: string;
}

const PublishersSection = ({ publishers = [], disabled, className }: IProps) => {
  const { control, setValue } = useFormContext();

  const { search, searchTerm, onSearchValueChange } = useListSearch();

  const displayedOptions = useMemo(() => {
    if (!searchTerm.length) {
      return publishers;
    }
    return publishers.filter(option => option.name.toLowerCase().includes(searchTerm));
  }, [searchTerm, publishers]);

  const value = useWatch({
    control,
    name: 'publisherIds',
  });

  //#region useMemo
  const allSelected = useMemo(
    () => (searchTerm.length ? value?.length === displayedOptions.length : value?.length === publishers.length),
    [value, publishers, displayedOptions, searchTerm],
  );

  const partialSelection = useMemo(() => searchTerm.length > 0, [searchTerm]);
  //#endregion

  //#region useCallback
  const setPublishers = useCallback(
    (ids: number[]) => {
      setValue('publisherIds', ids, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const valueSet = useMemo<Set<number>>(() => new Set(value ?? []), [value]);

  const isPublisherChecked = useCallback((id: number) => !!valueSet?.has(id), [valueSet]);

  const handleChange = useCallback(
    (selectedId: number) => {
      if (isPublisherChecked(selectedId)) {
        const ids = value.filter((id: number) => id !== selectedId) as number[];
        setPublishers(ids);
        return;
      }
      setPublishers([...(value || []), selectedId]);
    },
    [setPublishers, value, isPublisherChecked],
  );

  const handleClearOptions = useCallback(() => setPublishers([]), [setPublishers]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setPublishers(displayedOptions.map(option => option.id));
      } else {
        handleClearOptions();
      }
    },
    [displayedOptions, handleClearOptions, setPublishers],
  );

  const handleToggleSelectAll = useCallback(() => {
    handleSelectAll?.(!allSelected);
  }, [allSelected, handleSelectAll]);
  //#endregion

  return (
    <div className={classNames('flex flex-col', className)}>
      <div className='flex gap-2 mb-1'>
        <StyledInput
          fullWidth
          startAdornment={<SearchIcon className={`text-[${GREY}]`} />}
          autoFocus
          placeholder='Type to search'
          value={search}
          onChange={onSearchValueChange}
          sx={{ padding: '0 7px 0 16px' }}
        />
      </div>
      {displayedOptions.length ? (
        <>
          <SelectAllOption
            total={displayedOptions.length}
            indeterminate={!allSelected && !!value?.length}
            checked={allSelected}
            onChange={handleToggleSelectAll}
            label='Publisher'
            partialSelection={partialSelection}
            sx={{ flexGrow: 0 }}
          />
          <hr />
        </>
      ) : (
        <div className='m-auto'>No options</div>
      )}
      <FormGroup sx={{ overflowY: 'auto', flexWrap: 'nowrap' }}>
        {displayedOptions.map(({ name, id }) => (
          <FormControlLabel
            key={id}
            sx={{
              ':hover': {
                background: '#1b43321f',
              },
              fontSize: '16px',
              marginLeft: '12px',
              borderRadius: '6px',
            }}
            control={
              <Checkbox
                sx={{ padding: '9px' }}
                value={id}
                disabled={disabled}
                checked={isPublisherChecked(id)}
                onChange={() => handleChange(id)}
              />
            }
            label={name}
          />
        ))}
      </FormGroup>
    </div>
  );
};

export default PublishersSection;
